import { FC, useState, useEffect } from "react";
import { Badge, CloseButton, Container, Header, Label, Row, Section, SectionTitle, Title } from "./DebugOverlay.styles";
import { debugVisible } from "./debugLogger";

// ============================================================================
// Debug State Store (module-level)
// ============================================================================

export type DebugState = {
    authState: { status: string };
    permissionsRetry: RetryStateInfo;
    rolesRetry: RetryStateInfo;
};

type RetryStateInfo = {
    isRetrying: boolean;
    attemptCount: number;
    hasExhaustedRetries: boolean;
    lastStatus: number | null;
    config: { maxRetries: number };
};

const initialRetryState: RetryStateInfo = {
    isRetrying: false,
    attemptCount: 0,
    hasExhaustedRetries: false,
    lastStatus: null,
    config: { maxRetries: 3 },
};

let debugState: DebugState = {
    authState: { status: "idle" },
    permissionsRetry: initialRetryState,
    rolesRetry: initialRetryState,
};

export const setDebugState = (state: DebugState): void => {
    debugState = state;
};

export const getDebugState = (): DebugState => debugState;

// ============================================================================
// Components
// ============================================================================

const RetryInfo: FC<{ label: string; retry: RetryStateInfo }> = ({ label, retry }) => {
    const { maxRetries } = retry.config;
    return (
        <Section>
            <SectionTitle>{label}</SectionTitle>
            <Row>
                <Label>Attempt</Label>
                <span>
                    {retry.attemptCount}/{maxRetries}
                </span>
            </Row>
            <Row>
                <Label>Status</Label>
                <span>{retry.lastStatus ?? "-"}</span>
            </Row>
            <div>
                <Badge $active={retry.isRetrying} $variant="warning">
                    retrying
                </Badge>
                <Badge $active={retry.hasExhaustedRetries} $variant="error">
                    exhausted
                </Badge>
            </div>
        </Section>
    );
};

const getStatusColor = (status: string): string => {
    switch (status) {
        case "idle":
            return "#6b7280";
        case "oidc_loading":
        case "loading_permissions":
            return "#3b82f6";
        case "not_authenticated":
            return "#8b5cf6";
        case "retrying_permissions":
            return "#f59e0b";
        case "permissions_failed":
            return "#ef4444";
        case "ready":
            return "#22c55e";
        default:
            return "#6b7280";
    }
};

// polls the module-level debug state for updates
const useDebugState = (): DebugState => {
    const [state, setState] = useState<DebugState>(getDebugState);

    useEffect(() => {
        const interval = setInterval(() => {
            const current = getDebugState();
            setState(prev => {
                if (
                    prev.authState.status !== current.authState.status ||
                    prev.permissionsRetry.attemptCount !== current.permissionsRetry.attemptCount ||
                    prev.rolesRetry.attemptCount !== current.rolesRetry.attemptCount
                ) {
                    return current;
                }
                return prev;
            });
        }, 100);

        return () => clearInterval(interval);
    }, []);

    return state;
};

type Props = {
    namespace?: string;
};

export const DebugOverlay: FC<Props> = ({ namespace = "auth:flow" }) => {
    const [closed, setClosed] = useState(false);
    const { authState, permissionsRetry, rolesRetry } = useDebugState();

    // check if debug is enabled for this namespace
    if (!debugVisible(namespace)) return null;
    if (closed) return null;

    return (
        <Container data-id="debug-overlay">
            <Header>
                <Title>Auth Debug</Title>
                <CloseButton onClick={() => setClosed(true)}>✕</CloseButton>
            </Header>
            <Row>
                <Label>Status</Label>
                <span style={{ color: getStatusColor(authState.status), fontWeight: 600 }}>{authState.status}</span>
            </Row>
            <RetryInfo label="Permissions" retry={permissionsRetry} />
            <RetryInfo label="Roles" retry={rolesRetry} />
        </Container>
    );
};

DebugOverlay.displayName = "DebugOverlay";
